import type { DimensionUnit } from '../../types/dimensions';
import type { LtlAddress, LtlBookingConfirmation, LtlDocument, LtlShipment } from '../../types/ltl';
import type { WeightUnit } from '../../types/weight';

/**
 * Store-agnostic type contract for the LTL Freight "Freight Details" view.
 *
 * These types live in `@shipstation/client-common` and are intentionally pure:
 * they describe the shape of data and callbacks the presentational components
 * consume, with NO coupling to any data store (Redux/Zustand), networking
 * (axios/fetch), or realtime (Pusher) layer. Each host app maps its own store
 * shape onto {@link FreightDetailsProps} and supplies the callbacks. This keeps
 * the view reusable across ss-ui-client and shipstation-lite.
 */

/** Lifecycle status of a freight shipment, normalized from a backend status string. */
export const FreightShipmentStatus = {
  Booked: 'Booked',
  InTransit: 'InTransit',
  Delivered: 'Delivered',
  Cancelled: 'Cancelled',
  Exception: 'Exception',
} as const;
export type FreightShipmentStatus = (typeof FreightShipmentStatus)[keyof typeof FreightShipmentStatus];

/** Fine-grained tracking status IDs — mirrors dbo.FreightTrackingStatus and SS.Business.Freight.FreightTrackingStatusIds. */
export const FreightTrackingStatusId = {
  Unknown: 1,
  Created: 2,
  PendingPickup: 3,
  Dispatched: 4,
  InRouteToPickup: 5,
  AtPickup: 6,
  InTransit: 7,
  OutForDelivery: 8,
  AtDelivery: 9,
  Delivered: 10,
  Exception: 11,
  Voided: 12,
} as const;
export type FreightTrackingStatusId = (typeof FreightTrackingStatusId)[keyof typeof FreightTrackingStatusId];

/** Printable/downloadable document kinds associated with a freight shipment. */
export type FreightDocumentType =
  | 'BILL_OF_LADING'
  | 'PACKING_LIST'
  | 'PALLET_LABEL'
  | 'QUOTE'
  | 'PROOF_OF_DELIVERY'
  | 'WEIGHT_AND_INSPECTION'
  | 'ACCESSORIALS_RECEIPT'
  | 'BOL_PALLET_LABEL_COMBINED'
  | 'CERTIFICATE_OF_INSURANCE'
  | 'CERTIFICATE_OF_INSURANCE_INSTRUCTIONS'
  | 'CERTIFICATE_OF_ORIGIN_US'
  | 'CERTIFICATE_OF_ORIGIN_USMCA'
  | 'CERTIFICATE_OF_ORIGIN_USMCA_INSTRUCTIONS'
  | 'INTERNATIONAL_COMMERCIAL_INVOICE'
  | 'PALLET_LABEL_4X6';

/**
 * A single carrier tracking scan, mirroring `FreightTrackingEventContract` from
 * the `/freight/track` response. Sourced live from the freight provider
 * (WWEX/SpeedShip) on each track call.
 */
export type FreightTrackingEvent = {
  /** ISO 8601 timestamp of the carrier scan. */
  eventDateTimeUtc?: string | null;
  /** Fine-grained tracking status ID (dbo.FreightTrackingStatus). */
  freightTrackingStatusId?: number | null;
  /** Carrier-supplied source/scan code, e.g. `SAIA` — rendered as "Updated by {carrierCode}". */
  carrierCode?: string | null;
  /** Human-readable event text, e.g. `Picked up`. */
  description?: string | null;
};

/**
 * Live tracking for a shipment, mapped from a `FreightTrackingDetail` in the `/freight/track`
 * response. `events` come straight from the provider on each track call and exist only here —
 * the track path doesn't persist them to the entity, so they cannot be read off `LtlShipment`.
 * `trackingUrl` is persisted to the entity as well and available on `LtlShipment`, which is the
 * preferred source; this copy remains as a fallback until that value has synced there.
 *
 * Entity-backed tracking fields (`freightTrackingStatusId`, `lastTrackingEventUtc`) live on
 * `LtlShipment` instead, since they arrive with the shipment rather than with a track call.
 */
export type FreightTrackingHistory = {
  events?: FreightTrackingEvent[];
  trackingUrl?: string | null;
};

/**
 * A single note row on the Freight Details "Notes" card.
 *
 * The host resolves the label, so it owns the order-note-type catalog and the order rows are
 * listed in; this view renders the rows it is handed. Rows with no body should be omitted.
 */
export type OrderNote = {
  /** Localized note-type label, e.g. "Internal Note". Unique within a card. */
  label: string;
  /** The note text. */
  body: string;
};

/**
 * Props for the top-level Freight Details view (built in a later phase).
 *
 * All inputs are plain data + callbacks so the component stays presentational.
 */
export type FreightDetailsProps = {
  /** Whether the details drawer is currently open. */
  isOpen: boolean;
  /** The freight shipment being displayed. */
  shipment: LtlShipment;
  /** Booking confirmation details, when the shipment has been booked. */
  bookingConfirmation: LtlBookingConfirmation | null;
  /** Resolved origin address, when available. */
  originAddress: LtlAddress | null;
  /** Dimension unit to display measurement values in. */
  displayDimensionUnit: DimensionUnit;
  /** Weight unit to display weight values in. */
  displayWeightUnit: WeightUnit;
  /** ISO 4217 currency code for monetary values. */
  currencyCode?: string;
  /** Tracking history for the shipment (Phase 2). */
  trackingHistory?: FreightTrackingHistory | null;
  /** Notes on the order this shipment fulfills, rendered read-only. Omit when no order is linked. */
  notes?: OrderNote[];
  /**
   * Host-controlled stacking context for the drawer. Required — this drawer can be opened nested
   * inside the legacy (non-Mantine) Order Details drawer, whose z-index predates Mantine's global
   * defaults and needs a matching explicit value to render above it correctly.
   */
  zIndex: number;
  /** Whether documents are still being generated server-side (documents array will be empty while true). */
  isDocumentsLoading?: boolean;
  /** Invoked to print a specific document. */
  onPrintDocument: (document: LtlDocument) => void;
  /** Invoked to download a specific document. */
  onDownloadDocument: (document: LtlDocument) => void;
  /** Invoked to share a specific document. Omit until a backend share API is available — the Share button is hidden when absent. */
  onShareDocument?: (document: LtlDocument) => void;
  /** Document types expected for this shipment but not yet available (e.g. Proof of Delivery before delivery). */
  pendingDocumentTypes?: FreightDocumentType[];
  /** Invoked when the user shares the shipment. */
  onShare?: () => void;
  /** Invoked when the user prints all documents. */
  onPrintAll?: () => void;
  /** Invoked when the user shares all documents from the Documents section Actions menu. */
  onShareAll?: () => void;
  /** Invoked when the user downloads all documents. */
  onDownloadAll?: () => void;
  /** Fetches live tracking. Invoked from the Shipment Actions menu, and automatically when cached events are stale. */
  onTrack?: () => void;
  /** Invoked to close the drawer. */
  onClose: () => void;
  /** Invoked when the user initiates a void from the Shipment Actions menu. Only provide for Booked shipments. */
  onVoid?: () => void;
  /** Tooltip shown on the disabled Void menu item when the user lacks the FreightBook permission. Omit when void is unavailable for a non-permission reason (shipment not booked, already cancelled). */
  voidDisabledTooltip?: string;
  /** Order number to display as a link back to the related order. Omit when no order is linked. */
  orderNumber?: string;
  /** Invoked when the user clicks the order number link. */
  onOrderLinkClick?: () => void;
  /** Opens the Get Help modal. Omit to hide the menu item entirely (flag-off). */
  onGetHelp?: () => void;
  /** Renders as `data-amplitude-id` on the drawer title, for Amplitude Guide/Survey CSS-selector targeting. Omit to render no tracking attribute. */
  amplitudeTrackingId?: string;
};
